import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const verifyToken = useCallback(async (tokenToVerify) => {
        try {
            const response = await axiosInstance.get('/auth/verifytoken', {
                headers: {
                    Authorization: `Bearer ${tokenToVerify}`
                }
            });

            if (response.data.status === 200) {
                setUser(response.data.data.user);
                return true;
            }
            return false;
        } catch (err) {
            console.error('Token verification failed:', err);
            return false;
        }
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const storedToken = localStorage.getItem('token');
                if (storedToken) {
                    const isValid = await verifyToken(storedToken);
                    if (!isValid) {
                        logout();
                    }
                }
            } catch (err) {
                console.error('Auth initialization failed:', err);
                logout();
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, [verifyToken]);

    const login = async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.post('/auth/login', { email, password });

            if (response.data.status === 200) {
                const { token: newToken, user: userData } = response.data.data;
                localStorage.setItem('token', newToken);
                setToken(newToken);
                setUser(userData);
                return { success: true };
            }
            throw new Error(response.data.message || 'Login failed');
        } catch (err) {
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Login failed. Please try again.';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            login,
            logout,
            loading,
            error,
            verifyToken
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);